
import { Options, prop, Vue } from "vue-class-component";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import { MainAppController } from "@/app/ui/controllers/MainAppController";
import {
  ModalMessageEntities,
  OptionsClass,
  Snackbar
} from "@/domain/entities/MainApp";
import { RtcData, CreateCargoV3 } from "@/domain/entities/CargoV2";
import parsingErrorResponse from "@/app/infrastructures/misc/common-library/ParsingErrorResponse";
import {
  CalculateVolumeApiRequestV2,
  CalculateVolumeRequestV2,
  CreateCargoV3ApiRequest
} from "@/data/payload/api/CargoV2ApiRequest";
import {
  CALCULATE_TYPE,
  getConfigDimensionDivider,
  getConfigIsminimumVolumePerPiece,
  getConfigMinimumVolumePerPiece
} from "../modules/cargo-module";
import { AccountController } from "@/app/ui/controllers/AccountController";
import InputVehicleNo from "./input-vehicle-no.vue";
import { CourierManagementController } from "@/app/ui/controllers/CourierManagementController";
import { ApiRequestCourierManagementList } from "@/data/payload/api/CourierManagementApiRequest";
import { CourierManagementDetail } from "@/domain/entities/CourierManagement";
import { cloneDeep, debounce } from "lodash";
import { CheckEligible, useCargoV2 } from "../composables";
import Loading from "@/app/ui/components/loading/index.vue";

const { onCheckEligible } = useCargoV2();

class Props {
  modelValue = prop<boolean>({ default: false, type: Boolean });
  rtcData = prop<RtcData>({ default: new RtcData(), type: Object });
}

@Options({
  components: {
    InputVehicleNo,
    Loading
  },
  emits: ["update:modelValue", "refresh"],
  watch: {
    show(val: boolean) {
      if (val) {
        this.initModalData();
      }
    }
  }
})
class ModalBookingship extends Vue.with(Props) {
  isOpenSelectCargoProduct = false;
  bookingLoading = false;

  get show() {
    return this.modelValue;
  }

  set show(val: boolean) {
    this.$emit("update:modelValue", val);
  }

  onReset() {
    this.show = false;
  }

  initModalData() {
    CargoV2Controller.setForm({
      cargoVehicleNo: "",
      driverSelected: "",
      isValidVehicleNo: false,
      isValidAwbManual: false
    });
    this.isValidCargoVehicleNo = false;
    this.driver = new OptionsClass();
    this.driverOptions = [];
    this.loadingDriver = true;
  }
  get form() {
    return CargoV2Controller.form;
  }

  get commodityOptions(): OptionsClass[] {
    return CargoV2Controller.commodityOptions;
  }

  get disableCommodityGroup(): boolean {
    return this.commodityOptions.length === 1;
  }

  get listProductCargo() {
    const defaultChoices: any = [
      {
        name: this.$t("cargoV2.components.common.selectCargoProductType"),
        value: "",
        status: ""
      }
    ];
    const cargos = CargoV2Controller.cargoProductList.map((e: any) => ({
      name: e.cargoProductTypeCode,
      value: e.cargoProductTypeCode,
      status: e.cargoProductTypeStatus
    }));

    return defaultChoices.concat(cargos);
  }

  isValidCargoVehicleNo = false;
  get disabled() {
    const { cargoCommodity, productCargo, cargoNo } = this.form;
    return (
      !this.isValidCargoVehicleNo ||
      !cargoCommodity?.name ||
      !productCargo ||
      !cargoNo
    );
  }

  get rtcDetail() {
    return CargoV2Controller.rtcDetail;
  }

  onSelectCommodity(val: OptionsClass) {
    CargoV2Controller.form.cargoCommodity = val;
  }

  onOpenSelectCargoProduct() {
    this.isOpenSelectCargoProduct = true;
  }

  onCloseSelectCargoProduct() {
    this.isOpenSelectCargoProduct = false;
  }

  onSelectCargoProduct(_: string, value: string) {
    this.onCloseSelectCargoProduct();
    CargoV2Controller.updateSelectedCargoProduct(value);
  }

  // driver
  driver = new OptionsClass();
  loadingDriver = true;
  driverOptions: OptionsClass[] = [];


  fetchDriversDebounced = debounce(async (search: string, $t = this.$t) => {
    this.fetchDrivers(search, $t);
  }, 250).bind(this);

  async fetchDrivers(search = "", $t = this.$t) {
    if (!search.length || search.length > 2) {
      try {
        this.loadingDriver = true;
        this.driverOptions = [];

        const resp = await CourierManagementController.getList(
          new ApiRequestCourierManagementList({
            q: search,
            courierStatus: { name: "ACTIVE", value: "ACTIVE" }
          })
        );
        const data = resp.data.map(
          (item: CourierManagementDetail) =>
            new OptionsClass({
              label: `${item.vehiclePlate} - ${item.fullname}`,
              name: item.fullname,
              value: item.vehiclePlate,
              id: item.courierId
            })
        );

        this.driverOptions = [
          new OptionsClass({ label: $t("cargoV2.components.common.other"), value: "other" }),
          ...data
        ];
      } catch (err) {
        MainAppController.showErrorMessage(
          parsingErrorResponse(
            err,
            $t("cargoV2.components.modalBookingTruck.error.getCourierData"),
            this.fetchDrivers
          )
        );
      } finally {
        this.loadingDriver = false;
      }
    }
  }

  onSelectDriver(val: any) {
    if (val.value === "other") {
      this.form.isValidVehicleNo = false;
      this.form.cargoVehicleNo = "";
      this.form.driverSelected = val.value;
      CargoV2Controller.setForm(this.form);
      this.isValidCargoVehicleNo = false;
      return;
    }

    if (val === "") {
      this.form.isValidVehicleNo = false;
      this.form.cargoVehicleNo = "";
      this.form.driverSelected = "";
    this.isValidCargoVehicleNo = false;
      CargoV2Controller.setForm(this.form);
      return;
    }

    this.form.cargoVehicleNo = val.value;
    this.form.driverSelected = val.name;
    this.form.isValidVehicleNo = true;
    this.isValidCargoVehicleNo = true;
    CargoV2Controller.setForm(this.form);
  }

  onCloseCheckEligible() {
    this.$emit("closeCheckEligible")
  }
  onRefresh() {
    this.$emit("refresh");
  }
  onConfirmBooking() {
    this.show = false;

    const onSuccess = () => {
      MainAppController.closeMessageModal();
      MainAppController.showMessageModal(
        new ModalMessageEntities({
          image: "are-you-sure",
          title: this.$t("cargoV2.components.common.confirmCreationTitle"),
          message: this.$t("cargoV2.components.common.confirmCreationMessage"),
          textSuccess: this.$t("cargoV2.components.common.process"),
          textCancel: this.$t("cargoV2.components.common.back"),
          onSubmit: () => {
            onCheckEligible(
              new CheckEligible({
                success: this.onBooking,
                close: this.onCloseCheckEligible
              })
            );
          },
          onClose: () => {
            MainAppController.closeMessageModal();
          }
        })
      );
    };

    onCheckEligible(
      new CheckEligible({
        success: onSuccess,
        close: this.onCloseCheckEligible
      })
    );
  }

  async onCalculateVolume() {
    return await CargoV2Controller.calculateVolume(
      new CalculateVolumeApiRequestV2({
        calculateType: CALCULATE_TYPE.INIT,
        calculateRequest: new CalculateVolumeRequestV2({
          cargoType: "ship",
          totalPieces: this.rtcDetail.totalPieces,
          length: 90,
          width: 60,
          currentGrossWeight: this.rtcDetail.totalWeight,
          volumeWeightReweigh: this.form
            .totalAccumulationEstimationVolumeWeightDataTable,
          chargeableWeightReweigh: this.form.cargoTotalEstimateChargeableWeight,
        })
      })
    );
  }

  async onBooking() {
    try {
      this.bookingLoading = true;
      MainAppController.closeMessageModal();
      MainAppController.closeErrorMessage();

      await this.onCalculateVolume();

      CargoV2Controller.setDefaultDepartureArrivalNonPlane();

      const res = await CargoV2Controller.createBooking(
        new CreateCargoV3ApiRequest({
          ...this.form,
          cargoOverCutoffLimit: CargoV2Controller.cargoOverCutoffLimit
        })
      );

      this.onCreateResponse(res);
    } catch (error) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          error,
          AccountController.accountData.accountIsForeign
            ? this.$t("cargoV2.create.error.failedToCargoBooking")
            : this.$t("cargoV2.create.error.creationFailed"),

          this.onBooking,
          this.onRefresh
        )
      );
    } finally {
      this.bookingLoading = false;
    }
  }

  onCreateResponse(resp: CreateCargoV3) {
    const destination = cloneDeep(this.form.destinationCityCode).code;
    MainAppController.setSnackbarData(
      new Snackbar({
        type: "success",
        message: this.$t("cargoV2.create.snackbar.booked", {
          cargoNo: resp.cargoNo,
          destination
        })
      })
    );
    MainAppController.setAutoClose(true);
    MainAppController.setSnackbar(true);
    this.onRefresh();
  }
}

export default ModalBookingship;
